export const EVENT_TYPES = [
  { value: "fixed_time", label: "Fixed-Time Court", short: "Fixed" },
  { value: "session_based", label: "Session-Based Court", short: "Session" },
  { value: "morning", label: "Morning", short: "Morning" },
  { value: "non_court", label: "Non-Court Hearing", short: "Non-court" },
  { value: "post_court", label: "Post-Court", short: "Post" },
  { value: "all_day", label: "All Day", short: "All day" },
  { value: "out_of_station", label: "Out-of-Station", short: "Travel" },
];

export const SESSION_FLAGS = [
  { value: "session_1", label: "Session 1 — pre-lunch" },
  { value: "session_2", label: "Session 2 — post-lunch" },
  { value: "both", label: "Both sessions" },
];

export const POST_COURT_KINDS = [
  { value: "arbitration", label: "Arbitration" },
  { value: "client_meeting", label: "Client meeting" },
  { value: "call", label: "Call" },
  { value: "other", label: "Other" },
];

export const LOG_KINDS = [
  { value: "order", label: "Interim order" },
  { value: "direction", label: "Judicial direction" },
  { value: "adjournment", label: "Adjournment" },
  { value: "note", label: "Note" },
];

export const typeLabel = (v) => EVENT_TYPES.find((t) => t.value === v)?.label || v;

export const typeStyles = {
  fixed_time: { chip: "chip chip-fixed", bar: "bar-fixed", dot: "dot-fixed" },
  session_based: { chip: "chip chip-session", bar: "bar-session", dot: "dot-session" },
  morning: { chip: "chip chip-morning", bar: "bar-morning", dot: "dot-morning" },
  non_court: { chip: "chip chip-post", bar: "bar-post", dot: "dot-post" },
  out_of_station: { chip: "chip chip-oos", bar: "bar-oos", dot: "dot-oos" },
  post_court: { chip: "chip chip-post", bar: "bar-post", dot: "dot-post" },
  all_day: { chip: "chip chip-allday", bar: "bar-oos", dot: "dot-post" },
};

export const isAllDay = (event) =>
  event?.event_type === "all_day" || event?.event_type === "out_of_station";

/** The tiers that carry their own clock times. */
export const TIMED_TYPES = ["fixed_time", "non_court", "post_court", "morning"];

export const minutesOf = (hhmm) => {
  if (!hhmm || !hhmm.includes(":")) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

export const hhmm = (minutes) => {
  const m = Math.max(0, Math.min(Math.round(minutes), 23 * 60 + 59));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

/** Default end time for a fixed-time listing: the end of the session it starts in. */
export const sessionEndFor = (startTime, court) => {
  const start = minutesOf(startTime);
  if (start === null) return null;
  const boundaries = [
    minutesOf(court?.session_1_end) ?? 810,
    minutesOf(court?.session_2_end) ?? 990,
    minutesOf(court?.closes_at) ?? 1020,
  ];
  for (const b of boundaries) if (start < b) return hhmm(b);
  return hhmm(start + 60);
};

export const timeRange = (event) => {
  if (!event?.start_time) return null;
  return event.end_time
    ? `${fmtTime(event.start_time)} – ${fmtTime(event.end_time)}`
    : fmtTime(event.start_time);
};

export const whenLabel = (event) => {
  if (event?.start_time) return timeRange(event);
  if (event?.event_type === "out_of_station") return "Out of station";
  if (event?.event_type === "all_day") return "All day";
  if (event?.event_type === "morning") return "Morning";
  if (event?.event_type === "post_court") return "Post-court";
  const flag = event?.session_flag || event?.derived_session;
  return flag === "session_1"
    ? "Session 1"
    : flag === "session_2"
      ? "Session 2"
      : flag === "both"
        ? "Both sessions"
        : "All day";
};

/** When the day's first court listing starts — the morning slot runs up to it. */
export const firstCourtStart = (events = []) => {
  const times = events
    .filter((e) => e.event_type === "fixed_time" && e.start_time)
    .map((e) => e.start_time)
    .sort();
  return times[0] || null;
};

export const morningLabel = (events = []) => {
  const first = firstCourtStart(events);
  return first ? `Morning · before ${fmtTime(first)}` : "Morning";
};

export const plusHour = (start) => {
  const mins = minutesOf(start);
  return mins === null ? null : hhmm(mins + 60);
};

export const sessionLabel = (v) =>
  v === "session_1" ? "Session 1" : v === "session_2" ? "Session 2" : v === "both" ? "Both sessions" : null;

export const heatClass = (level) => ["heat-0", "heat-1", "heat-2", "heat-3", "heat-4"][level || 0];

export const fmtTime = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${suffix}`;
};

export const isoDate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
};

export const parseISO = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

export const addMonths = (date, n) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
};

export const startOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

export const longDate = (d) =>
  (d instanceof Date ? d : parseISO(d)).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const shortDate = (d) =>
  (d instanceof Date ? d : parseISO(d)).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

export const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 7; h <= 21; h += 1) {
    for (const m of [0, 15, 30, 45]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();
